import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight, ArrowsLeftRight, Bell, BookOpenText, CalendarBlank, CaretDown,
  ChartLineUp, CheckCircle, CookingPot, CurrencyDollar, DotsThreeVertical,
  FileText, ListChecks, Scales, Target,
} from '@phosphor-icons/react';
import { ResponseBlocks, ResponseSections } from './ResponseRichText';

const CARD_CONFIG = {
  analysis: { label: 'Análise', Icon: ChartLineUp, tone: 'blue' },
  report: { label: 'Relatório', Icon: FileText, tone: 'blue' },
  plan: { label: 'Plano do dia', Icon: CalendarBlank, tone: 'violet' },
  decision: { label: 'Decisão', Icon: Scales, tone: 'yellow' },
  reminder: { label: 'Lembrete', Icon: Bell, tone: 'rose' },
  comparison: { label: 'Comparação', Icon: ArrowsLeftRight, tone: 'blue' },
  recipe: { label: 'Receita', Icon: CookingPot, tone: 'green' },
  finance: { label: 'Finanças', Icon: CurrencyDollar, tone: 'green' },
  study: { label: 'Estudo', Icon: BookOpenText, tone: 'violet' },
  task: { label: 'Tarefa', Icon: ListChecks, tone: 'blue' },
  calendar: { label: 'Agenda', Icon: CalendarBlank, tone: 'violet' },
  structured: { label: 'Organizado', Icon: Target, tone: 'blue' },
};

function cardConfig(mode) {
  return CARD_CONFIG[mode] || CARD_CONFIG.structured;
}

function formatWhen(value) {
  if (!value) return 'Agora';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(date);
  } catch {
    return 'Agora';
  }
}

function CardHeader({ presentation, timestamp }) {
  const config = cardConfig(presentation.mode);
  const Icon = config.Icon;
  const title = presentation.card?.title || presentation.title;
  const summary = presentation.card?.summary || presentation.summary;
  const when = presentation.card?.when || timestamp;
  return (
    <header className="athena-structured-card-header">
      <div className="athena-structured-card-topline">
        <div className="athena-structured-card-label">
          <span className="athena-card-label-icon"><Icon size={18} weight="regular" aria-hidden="true" /></span>
          <span>{presentation.card?.eyebrow || config.label}</span>
        </div>
        <div className="athena-structured-card-meta">
          <time>{formatWhen(when)}</time>
          <DotsThreeVertical size={18} weight="bold" aria-hidden="true" />
        </div>
      </div>
      {title && <h2>{title}</h2>}
      {summary && <p>{summary}</p>}
    </header>
  );
}

function CardItems({ items = [], numbered = false, checklist = false }) {
  if (!items.length) return null;
  return (
    <ol className={`athena-structured-card-items ${numbered ? 'is-numbered' : ''} ${checklist ? 'is-checklist' : ''}`}>
      {items.map((item, index) => (
        <li key={`${item.text}-${index}`}>
          {numbered ? (
            <span className="athena-card-item-number">{String(index + 1).padStart(2, '0')}</span>
          ) : (
            <CheckCircle size={17} weight={item.checked || checklist ? 'fill' : 'regular'} aria-hidden="true" />
          )}
          <span className="athena-card-item-copy">
            <strong>{item.text}</strong>
            {item.detail && <small>{item.detail}</small>}
          </span>
          {item.status && <em>{item.status}</em>}
        </li>
      ))}
    </ol>
  );
}

const ANALYSIS_ICONS = [Target, ArrowsLeftRight, ChartLineUp];

function AnalysisSignals({ items = [] }) {
  if (!items.length) return null;
  const visibleItems = items.slice(0, 3);
  return (
    <div className={`athena-analysis-signals count-${visibleItems.length}`} aria-label="Pontos-chave da análise">
      {visibleItems.map((item, index) => {
        const Icon = ANALYSIS_ICONS[index] || ChartLineUp;
        return (
          <React.Fragment key={`${item.text}-${index}`}>
            <div className="athena-analysis-signal">
              <Icon size={25} weight="thin" aria-hidden="true" />
              <strong>{item.text}</strong>
              {item.detail && <span>{item.detail}</span>}
            </div>
            {index < visibleItems.length - 1 && <ArrowRight className="athena-analysis-arrow" size={19} weight="light" aria-hidden="true" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function DecisionChoices({ comparison }) {
  if (!comparison?.left || !comparison?.right) return null;
  const choices = [comparison.left, comparison.right];
  return (
    <div className="athena-decision-choices" role="group" aria-label="Comparação de opções">
      {choices.map((choice, index) => (
        <React.Fragment key={`${choice.title}-${index}`}>
          {index === 1 && <span className="athena-decision-versus" aria-hidden="true">VS</span>}
          <section>
            <h3>{choice.title}</h3>
            <ul>{choice.points.map((point, pointIndex) => <li key={pointIndex}><CheckCircle size={13} weight="fill" aria-hidden="true" />{point}</li>)}</ul>
          </section>
        </React.Fragment>
      ))}
    </div>
  );
}

function CardActions({ actions, onAction }) {
  if (!actions?.length) return null;
  return (
    <div className="athena-card-actions">
      {actions.map(action => (
        <button
          key={action.id}
          type="button"
          className={action.tone === 'primary' ? 'is-primary' : ''}
          onClick={() => onAction?.(action.id)}
        >
          {action.label}
        </button>
      ))}
    </div>
  );
}

function CardDetails({ presentation, compact = false }) {
  return (
    <>
      {presentation.message?.length > 0 && <ResponseBlocks blocks={presentation.message} compact={compact} />}
      {presentation.sections?.length > 0 && <ResponseSections sections={presentation.sections} compact={compact} />}
    </>
  );
}

function PlanGentleNote({ presentation }) {
  const firstDetail = presentation.card?.items?.find(item => item.detail)?.detail;
  return (
    <aside className="athena-plan-gentle-note">
      <Bell size={18} weight="regular" aria-hidden="true" />
      <div>
        <span>Lembrete</span>
        <p>{firstDetail || 'Um passo de cada vez.'}</p>
      </div>
    </aside>
  );
}

function ExpandControl({ presentation, expanded, onToggle }) {
  const config = cardConfig(presentation.mode);
  const title = presentation.card?.title || presentation.title || config.label;
  if (!presentation.expandable || !presentation.fullContent?.length) return null;
  return (
    <>
      <button
        type="button"
        className="athena-expand-button"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Recolher' : 'Ver'} ${title}`}
        onClick={onToggle}
      >
        <span>{expanded ? 'Ver resumo' : `Ver ${config.label.toLowerCase()} completa`}</span>
        <CaretDown size={18} aria-hidden="true" className={expanded ? 'is-expanded' : ''} />
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            className="athena-expand-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            <ResponseSections sections={presentation.fullContent} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function CardFrame({ presentation, timestamp, children }) {
  const config = cardConfig(presentation.mode);
  return (
    <article className={`athena-structured-card tone-${config.tone} mode-${presentation.mode}`}>
      <CardHeader presentation={presentation} timestamp={timestamp} />
      {children}
    </article>
  );
}

export function AnalysisCard({ presentation, expanded, onToggle, onAction, timestamp }) {
  const items = presentation.card?.items || [];
  return (
    <CardFrame presentation={presentation} timestamp={timestamp}>
      <AnalysisSignals items={items} />
      {items.length > 3 && <CardItems items={items.slice(3)} />}
      <CardDetails presentation={presentation} />
      <CardActions actions={presentation.card?.actions} onAction={onAction} />
      <ExpandControl presentation={presentation} expanded={expanded} onToggle={onToggle} />
    </CardFrame>
  );
}

export function PlanCard({ presentation, expanded, onToggle, onAction, timestamp }) {
  return (
    <CardFrame presentation={presentation} timestamp={timestamp}>
      <CardItems items={presentation.card?.items} numbered />
      <PlanGentleNote presentation={presentation} />
      <CardDetails presentation={presentation} />
      <CardActions actions={presentation.card?.actions} onAction={onAction} />
      <ExpandControl presentation={presentation} expanded={expanded} onToggle={onToggle} />
    </CardFrame>
  );
}

export function ReminderCard({ presentation, expanded, onToggle, onAction, timestamp }) {
  return (
    <CardFrame presentation={presentation} timestamp={timestamp}>
      {presentation.card?.items?.length > 0 && <h3 className="athena-reminder-list-label">Eu vou</h3>}
      <CardItems items={presentation.card?.items} checklist />
      <CardDetails presentation={presentation} compact />
      <CardActions actions={presentation.card?.actions} onAction={onAction} />
      <ExpandControl presentation={presentation} expanded={expanded} onToggle={onToggle} />
    </CardFrame>
  );
}

export function DecisionCard({ presentation, expanded, onToggle, onAction, timestamp }) {
  const comparison = presentation.card?.comparison;
  return (
    <CardFrame presentation={presentation} timestamp={timestamp}>
      <DecisionChoices comparison={comparison} />
      {!comparison && <CardItems items={presentation.card?.items} />}
      <CardDetails presentation={presentation} />
      <CardActions actions={presentation.card?.actions} onAction={onAction} />
      <ExpandControl presentation={presentation} expanded={expanded} onToggle={onToggle} />
    </CardFrame>
  );
}

export function GenericStructuredCard({ presentation, expanded, onToggle, onAction, content, timestamp }) {
  return (
    <CardFrame presentation={presentation} timestamp={timestamp}>
      <CardItems items={presentation.card?.items} />
      <CardDetails presentation={presentation} />
      {!presentation.message?.length && !presentation.sections?.length && content && <p className="athena-structured-fallback-copy">{content}</p>}
      <CardActions actions={presentation.card?.actions} onAction={onAction} />
      <ExpandControl presentation={presentation} expanded={expanded} onToggle={onToggle} />
    </CardFrame>
  );
}
